import React, { useEffect } from "react";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { modalStatusAtom } from "@/atom/modal";
import { useModal } from "@/hook";
import { CloseIcon } from "@/assets/svgs/icons";


interface ModalProps {
    id: string;
    title?: React.ReactNode;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    className?: string;
    onClose?: () => void;
    children?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ((props) => {
    const {
        id,
        title,
        size = "lg",
        className: propsClassName,
        onClose,
        children,
    } = props;

    const modal = useModal();
    const modalStatus = useAtomValue(modalStatusAtom);

    const handleClose = () => {
        modal.close(id);
    };

    useEffect(() => {
        if (!modalStatus[id]) {
            onClose?.();
        }
        // eslint-disable-next-line
    }, [modalStatus[id]]);

    return (
        <Dialog as="div"
                className="relative z-[1000]"
                open={modalStatus[id] ?? false}
                onClose={handleClose}
        >
            <DialogBackdrop transition
                            className="fixed inset-0 bg-black bg-opacity-25 duration-300 ease-out data-[closed]:opacity-0"
            />

            <div className="fixed inset-0">
                <div className="min-h-full p-4 flex items-center justify-center">
                    <DialogPanel transition
                                 data-testid="modal-panel"
                                 className={classNames(
                                     `modal-${size}`,
                                     "h-fit flex flex-col scrollbar-hide",
                                     "duration-300 ease-out data-[closed]:opacity-0 data-[closed]:scale-95",
                                     propsClassName ?? ""
                                 )}
                    >
                        <DialogTitle as="h3" className="modal-header relative z-30">
                            {title}
                            <button onClick={handleClose}>
                                <CloseIcon className="size-5" />
                            </button>
                        </DialogTitle>
                        {children}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
});

export default Modal;
